import { Link } from 'gatsby';
import { useStaticQuery, graphql } from 'gatsby';
import { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import classNames from 'classnames';
import { Helmet } from 'react-helmet';
// import arrowUp from '../../images/icons/icon-top-chevron.svg';
import arrowUp from '../../images/icons/icon-squat-chevron.svg';
import { VoterGuideButton } from './voter-guide-button';
import { twMerge } from 'tailwind-merge';

const HeaderLink =
  'text-base whitespace-nowrap font-bold text-gray-800 transition-all border-b border-white md:hover:text-brand-blue-4 md:hover:border-b md:hover:border-brand-green-3 inline-block break-inside-avoid cursor-pointer';
const HeaderButton =
  'text-brand-blue-5 text-xs transition-all hover:scale-105 bg-gradient-to-r from-brand-green-3 via-brand-green-3 to-brand-yellow-2 bg-size-200 bg-pos-0 hover:bg-pos-100 focus:bg-brand-green-2 focus:bg-none focus:scale-100 inline-block font-bold px-6 py-4 uppercase rounded-sm break-inside-avoid lg:text-white lg:from-brand-blue-4 lg:via-brand-blue-4 lg:to-brand-green-3 lg:focus:bg-transparent lg:focus:bg-gradient-to-r lg:hover:scale-110';

const DesktopMenu = ({ menu, headerOptions }) => (
  <Fragment>
    {menu.map((item) => {
      if (item.children !== undefined) {
        // If there are children, render a dropdown
        return (
          <li
            key={item.name}
            className={twMerge(
              HeaderLink,
              'group',
              headerOptions?.blueDesktop &&
                'border-none md:hover:text-white md:hover:border-b md:hover:border-white'
            )}
          >
            <div className="relative">
              <div className="flex flex-row gap-1 break-inside-avoid cursor-pointer py-3">
                {item.name}
                <img
                  src={arrowUp}
                  width={12}
                  height={12}
                  alt="arrowUp"
                  className="group-hover:rotate-180 rotate-0 transition-all duration-200 ml-2"
                />
              </div>
              <div
                className="hidden min-w-fit group-hover:block z-40 absolute left-1/2 -translate-x-1/2 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 overflow-clip w-40"
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="options-menu"
              >
                <div className="min-w-fit" role="none">
                  <ul className="min-w-fit">
                    {item.children.map((child) => (
                      <li key={child.name}>
                        <child.Type
                          className="bg-brand-blue-5 p-3 text-white hover:text-brand-green-3 focus:text-brand-green-3 hover:underline focus:underline text-center block text-sm"
                          key={`${child.name}-link`}
                          to={child.href}
                          role="menuitem"
                        >
                          {child.name}
                        </child.Type>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </li>
        );
      }
      if (item.class == HeaderButton) {
        return (
          <li key={item.name}>
            <item.Type
              className={classNames(
                HeaderButton,
                headerOptions?.blueDesktop &&
                  'lg:from-brand-blue-5 lg:via-brand-blue-5 lg:!to-brand-blue-4'
              )}
              to={item.href}
            >
              {item.name}
            </item.Type>
          </li>
        );
      } else {
        return (
          <li
            key={item.name}
            className={
              item.class ||
              twMerge(
                HeaderLink,
                headerOptions?.blueDesktop &&
                  'border-b-0 md:hover:text-white md:hover:border-b md:hover:border-white'
              )
            }
          >
            <item.Type to={item.href}>{item.name}</item.Type>
          </li>
        );
      }
    })}
  </Fragment>
);

const MobileMenu = ({ menu }) => (
  <Fragment>
    {menu.map((item) => {
      if (item.children !== undefined) {
        return (
          <Fragment key={item.name}>
            <li className="text-base whitespace-nowrap font-bold text-brand-blue-3 inline-block break-inside-avoid">
              {item.name}
            </li>
            <ul className="ml-6">
              {item.children.map((child) => (
                <li
                  key={child.name}
                  className="text-gray-100 block cursor-pointer text-md py-1 hover:text-brand-green-3 focus:text-brand-green-3 hover:underline focus:underline"
                >
                  <child.Type key={`${child.name}-link`} to={child.href}>
                    {child.name}
                  </child.Type>
                </li>
              ))}
            </ul>
          </Fragment>
        );
      }
      if (item.class == HeaderButton) {
        return (
          <li key={item.name}>
            <item.Type className={`${HeaderButton} w-full text-center py-5`} to={item.href}>
              {item.name}
            </item.Type>
          </li>
        );
      } else {
        return (
          <li
            key={item.name}
            className="text-base whitespace-nowrap font-bold text-gray-100 inline-block break-inside-avoid py-2 cursor-pointer  hover:text-brand-green-3 focus:text-brand-green-3 hover:underline focus:underline"
          >
            <item.Type to={item.href}>{item.name}</item.Type>
          </li>
        );
      }
    })}
  </Fragment>
);

const Header = ({ headerOptions }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [animate, setAnimate] = useState(false);
  useEffect(() => {
    setTimeout(() => setAnimate(mobileOpen), 100);
  }, [mobileOpen]);

  const menu = [
    {
      name: 'Mission',
      href: '/mission/',
      Type: Link,
    },
    {
      name: 'Issues',
      href: '/issues/',
      Type: Link,
    },
    {
      name: 'Team',
      href: '/team/',
      Type: Link,
    },
    {
      name: 'Talent',
      href: '/talent/',
      Type: Link,
    },
    {
      name: 'Polls',
      href: '/pulse/',
      Type: Link,
    },
    {
      name: 'News',
      href: '/news/',
      Type: Link,
    },
    {
      name: 'Research',
      href: '/research/',
      Type: Link,
    },
    {
      name: 'Voter Guide', // Note: non-breaking space is important! ' '
      href: '/voter-guide/',
      Type: Link,
      class: HeaderButton,
    },
  ];

  const Logos = useStaticQuery(
    graphql`
      query {
        MainLogo: file(relativePath: { eq: "logo-clean.svg" }) {
          publicURL
        }
        HomeLogo: file(relativePath: { eq: "logo-clean-dark-white.svg" }) {
          publicURL
        }
        MainLogoWhite: file(relativePath: { eq: "logo-clean-white.svg" }) {
          publicURL
        }
      }
    `
  );

  return (
    <Fragment>
      <Helmet>
        {/* Google Structured Data */}
        <script type="application/ld+json">
          {`
[
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "url": "http://growsf.org",
    "logo": "https://growsf.org/media-kit/icon.png",
    "founder": [
      {
        "@type":"Person",
        "name":"Sachin Agarwal",
        "url":"https://growsf.org/team/#sachin-agarwal",
        "sameAs":[
          "https://twitter.com/agarwal",
          "https://isachin.com/"
        ]
      },
      {
        "@type":"Person",
        "name":"Steven Buss Bacio",
        "url":"https://growsf.org/team/#steven-bacio",
        "sameAs":[
          "https://twitter.com/sbuss",
          "https://stevenbuss.com"
        ]
      }
    ],
    "foundingDate": "2020-10-01",
    "slogan": "GrowSF works for a safer, cleaner, and more affordable San Francisco"
  },
  {
    "@context" : "https://schema.org",
    "@type" : "WebSite",
    "name" : "GrowSF",
    "url" : "https://growsf.org/"
  }
]
            `}
        </script>
      </Helmet>
      {/* <Banner
        Title="Vote with GrowSF this November."
        URL="/voter-guide"
        LinkText="Read our voter guide here."
      /> */}
      <div
        className={`relative ${headerOptions?.blueMobile ? 'bg-brand-blue-3' : 'bg-white'} ${
          headerOptions?.blueDesktop ? 'lg:bg-brand-blue-3' : 'lg:bg-white'
        }`}
      >
        <div
          className={`relative z-20 flex justify-between items-center max-w-7xl mx-auto px-4 py-6 sm:px-6 md:justify-start md:space-x-10 lg:px-8 ${
            headerOptions?.borderless ? '' : 'border-b border-gray-75'
          }`}
        >
          <div className="flex justify-start md:w-0 md:flex-1 items-center">
            <Link className="flex shrink-0" to="/">
              <span className="sr-only">GrowSF</span>
              <img
                className="h-8 w-auto sm:h-10 lg:hidden"
                src={
                  headerOptions?.blueMobile ? Logos.HomeLogo.publicURL : Logos.MainLogo.publicURL
                }
                alt="GrowSF logo"
              />
              <img
                className="hidden h-8 w-auto sm:h-10 lg:block"
                src={
                  headerOptions?.blueDesktop ? Logos.HomeLogo.publicURL : Logos.MainLogo.publicURL
                }
                alt="GrowSF logo"
              />
            </Link>
          </div>
          {/* Start Mobile Menu Button */}
          <div className="flex h-full my-auto lg:hidden">
            <button
              type="button"
              className={`rounded-md inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 ${
                headerOptions?.blueMobile
                  ? 'text-brand-blue-5'
                  : 'bg-white text-gray-400 hover:text-gray-700 hover:bg-gray-100'
              }`}
              aria-expanded="false"
              onClick={() => setMobileOpen(!mobileOpen) && console.log('clicked')}
            >
              <span className="sr-only">Open menu</span>
              <svg
                className="h-8 w-8"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
          {/* End Mobile Menu Button */}
          <div className="hidden lg:flex-1 lg:flex lg:items-center lg:justify-end">
            <nav>
              <ul className="flex flex-initial justify-between xl:space-x-16 lg:space-x-10 space-x-8 items-center">
                <DesktopMenu menu={menu} headerOptions={headerOptions} />
              </ul>
            </nav>
          </div>
          {/* <VoterGuideButton /> */}
        </div>

        <div
          className={`absolute top-0 inset-x-0 p-2 transition transform origin-top-right lg:hidden	${
            mobileOpen ? 'z-20' : 'hidden'
          }`}
        >
          <div
            className={`rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 bg-brand-blue-5 divide-y-2 divide-gray-50 origin-top ${
              animate
                ? 'transition ease-out duration-200 opacity-100 translate-y-0 scale-100'
                : 'transition ease-in duration-150 opacity-0 translate-y-1 scale-95'
            }`}
          >
            <div className="pt-3 pb-6 px-2">
              <div className="flex items-center justify-between mx-4 pt-2 pb-4 border-b border-brand-blue-3">
                <div>
                  <Link className="flex" to="/">
                    <span className="sr-only">GrowSF</span>
                    <img
                      className="h-8 w-auto sm:h-10"
                      src={Logos.MainLogoWhite.publicURL}
                      alt="GrowSF logo"
                    />
                  </Link>
                </div>
                <div className="-mr-2">
                  <button
                    type="button"
                    className="p-2 inline-flex items-center justify-center text-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                    onClick={() => setMobileOpen(!mobileOpen) && console.log('clicked')}
                  >
                    <span className="sr-only">Close menu</span>
                    <svg
                      className="h-8 w-8"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="mt-6 px-2">
                <nav>
                  <ul className="grid gap-3">
                    <MobileMenu menu={menu} />
                  </ul>
                </nav>
              </div>
            </div>
            {/* <div className="py-6 px-5">
              <div className="mt-6">
                <Link
                  to="/voter-guide"
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-md font-bold text-base font-medium text-white bg-brand-blue-3 hover:bg-blue-700"
                >
                  Voter Guide
                </Link>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

Header.propTypes = {
  siteTitle: PropTypes.string,
  description: PropTypes.string,
  previewImage: PropTypes.shape({
    src: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  location: PropTypes.string,
};

Header.defaultProps = {
  siteTitle: '',
  description: '',
  location: '',
};

export default Header;
